import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import deleteIcon from 'assets/delete_icon.svg';
import BasicTableNoPagination from 'components/layout/tables/BasicTableNoPagination';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getPositionHours, {
  isAverageHourlyEarningsMissing,
} from 'services/offers/getPositionHours';
import getWorkTypeFigures, {
  IWorkTypeFigures,
} from 'services/offers/getWorkTypeFigures';

import 'style/offers/MarginRatio.css';
import 'style/offers/OfferProductPriceSummary.css';
import getShownDifference from 'services/numbers/getShownDifference';
import getContributionMarginTotal from 'services/products/calculations/getContributionMarginTotal';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import translations from 'services/translations/translations';

import { getMarginClass } from '../offerProduct/calculationWindow/getMarginRatioClassName';
import RowOrder from '../offerProduct/calculationWindow/RowOrder';

/**
 * The position code (e.g. VS1) as a field while the order is changed: saved when the field
 * is left or Enter is pressed, Escape returns the saved code.
 */
const PositionCodeInput = ({
  code,
  save: saveCode,
}: {
  code: string;
  save: (position: string) => Promise<boolean>;
}): JSX.Element => {
  const [value, setValue] = useState(code);
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(code), [code]);

  const save = async () => {
    const trimmed = value.trim();

    if (trimmed === code || trimmed === '' || saving) {
      setValue(code);
      return;
    }

    setSaving(true);
    const saved = await saveCode(trimmed);
    setSaving(false);

    if (!saved) {
      setValue(code);
    }
  };

  return (
    <Input
      aria-label={code}
      bsSize="sm"
      className="position-code-input"
      disabled={saving}
      onBlur={save}
      onChange={(event) => setValue(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          setValue(code);
        }
      }}
      value={value}
    />
  );
};

/**
 * The positions of the offer in the order of the offer PDF, a line between the work
 * types. The columns are in the order of the summary row of the calculation window:
 * per unit, totals, margin %, then hours.
 */
const OfferProductsTable = ({
  move,
  priceList = false,
  products,
  remove,
  rename,
  reordering = false,
  type,
  money,
  workTypeSummary = false,
}: {
  // Removes a row; without it the rows have no trash can
  remove?: (id: number) => void;
  // Moves a position within its work type (indexes within the work type)
  move?: (group: string, fromIndex: number, toIndex: number) => void;
  // The rows of the additional price list: no codes, the price printed in the PDF shown
  priceList?: boolean;
  products: IOfferProductsGet[];
  // Changes the code of a position (e.g. VS1) while the order is changed; false when the
  // code was not saved
  rename?: (id: number, position: string) => Promise<boolean>;
  // The number of each position within its work type is shown for changing the order
  reordering?: boolean;
  type: string;
  // The money shown: its sign and the conversion of the amounts calculated in the
  // currency of the offer (only for viewing); without it euros as they are
  money?: { convert: (value: number) => number; symbol: string };
  // The sums by work type and of the whole offer below the positions, in the same columns
  workTypeSummary?: boolean;
}): JSX.Element | null => {
  const history = useHistory();

  const { t } = useTranslation([
    translations.common,
    translations.offers,
    translations.products,
  ]);

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories ?? []
  );

  const symbol = money?.symbol ?? '€';
  const removeLabel = priceList
    ? t(`${translations.offers}:priceList.Delete row`)
    : t(`${translations.offers}:Delete position`);
  const shown = (value: number) => (money ? money.convert(value) : value);

  const getRow = (offerProduct: IOfferProductsGet) => {
    const unit = t(`${translations.common}:units:${offerProduct.unit}`);
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );
    const quantity = `${formatGroupedNumber(
      offerProduct.quantity,
      2,
      0
    )} ${unit}`;

    const row = {
      averageHourlyEarningsMissing: false,
      contributionMargin: '',
      contributionMarginPerHour: '',
      contributionMarginPerUnit: '',
      contributionMarginRatio: '',
      expensePerUnit: '',
      expenseTotal: '',
      group: offerProduct.group,
      hours: '',
      id: offerProduct.id,
      // The marks of the margin % as in the calculation window
      marginClass: '',
      name: offerProduct.name,
      position: offerProduct.position,
      pricePerUnit: '',
      priceTotal: '',
      // A price list row: what the offer PDF shows as its price
      printedPrice: offerProduct.priceText ?? '',
      quantity,
    };

    if (primaryVersion === undefined) {
      return row;
    }

    const { installations, subproducts } = primaryVersion;
    const perUnit = (value: number) =>
      `${formatGroupedNumber(shown(value))} ${symbol}/${unit}`;

    // Without a calculation and a price text a price list row is a note
    if (priceList && installations.length === 0 && subproducts.length === 0) {
      return row;
    }

    if (row.printedPrice === '') {
      row.printedPrice = perUnit(
        getSellingPricePerUnit(installations, subproducts)
      );
    }
    const euros = (value: number) =>
      `${formatGroupedNumber(shown(value))} ${symbol}`;

    const contributionMargin = getContributionMarginTotal(
      installations,
      subproducts,
      offerProduct.quantity
    );
    const hours = getPositionHours(
      installations,
      primaryVersion.averageHourlyEarnings,
      offerProduct.quantity,
      installationCategories
    );

    return {
      ...row,
      averageHourlyEarningsMissing: isAverageHourlyEarningsMissing(
        installations,
        primaryVersion.averageHourlyEarnings,
        installationCategories
      ),
      // The margins shown: the shown selling price minus the shown expense, so they add up
      contributionMargin: euros(
        getShownDifference(
          getSellingPriceTotal(
            installations,
            subproducts,
            offerProduct.quantity
          ),
          getExpenseTotal(installations, subproducts, offerProduct.quantity)
        )
      ),
      contributionMarginPerHour:
        hours === 0
          ? ''
          : `${formatGroupedNumber(
              shown(contributionMargin / hours)
            )} ${symbol}/h`,
      contributionMarginPerUnit: perUnit(
        getShownDifference(
          getSellingPricePerUnit(installations, subproducts),
          getExpensePerUnit(installations, subproducts)
        )
      ),
      contributionMarginRatio: `${formatGroupedNumber(
        getContributionMarginRatio(installations, subproducts)
      )} %`,
      marginClass:
        getSellingPricePerUnit(installations, subproducts) === 0
          ? ''
          : getMarginClass(
              getContributionMarginRatio(installations, subproducts)
            ),
      expensePerUnit: perUnit(getExpensePerUnit(installations, subproducts)),
      expenseTotal: euros(
        getExpenseTotal(installations, subproducts, offerProduct.quantity)
      ),
      hours: hours === 0 ? '' : `${formatGroupedNumber(hours)} h`,
      pricePerUnit: perUnit(getSellingPricePerUnit(installations, subproducts)),
      priceTotal: euros(
        getSellingPriceTotal(installations, subproducts, offerProduct.quantity)
      ),
    };
  };

  // The place of each position within its work type, for changing the order
  const displayedProducts = products.map((offerProduct, index) => {
    const sameGroup = products.filter(
      (other) => other.group === offerProduct.group
    );

    return {
      ...getRow(offerProduct),
      groupCount: sameGroup.length,
      groupIndex: products
        .slice(0, index)
        .filter((other) => other.group === offerProduct.group).length,
    };
  });

  // The sums by work type in the order of the positions, the whole offer last: rows of
  // their own below the positions, so their figures are in the same columns
  const summaryRow = (
    summary: 'group' | 'total',
    id: string,
    title: string,
    figures: IWorkTypeFigures
  ) => {
    const ratio =
      figures.sellingPrice === 0
        ? 0
        : (figures.contributionMargin / figures.sellingPrice) * 100;
    const euros = (value: number) =>
      `${formatGroupedNumber(shown(value))} ${symbol}`;

    return {
      averageHourlyEarningsMissing: false,
      contributionMargin: euros(
        // The shown selling price minus the shown expense, so the row adds up
        getShownDifference(figures.sellingPrice, figures.expense)
      ),
      contributionMarginPerHour:
        figures.hours === 0
          ? '-'
          : `${formatGroupedNumber(
              shown(figures.contributionMargin / figures.hours)
            )} ${symbol}/h`,
      contributionMarginPerUnit: '',
      contributionMarginRatio: `${formatGroupedNumber(ratio)} %`,
      expensePerUnit: '',
      expenseTotal: euros(figures.expense),
      group: '',
      groupCount: 0,
      groupIndex: -1,
      hours: `${formatGroupedNumber(figures.hours)} h`,
      id,
      marginClass: figures.sellingPrice === 0 ? '' : getMarginClass(ratio),
      name: title,
      position: '',
      pricePerUnit: '',
      priceTotal: euros(figures.sellingPrice),
      printedPrice: '',
      quantity:
        Object.entries(figures.quantities)
          .map(
            ([unit, quantity]) =>
              `${formatGroupedNumber(quantity, 2, 0)} ${t(
                `${translations.common}:units:${unit}`
              )}`
          )
          .join(', ') || '-',
      summary,
    };
  };

  const totalFigures = getWorkTypeFigures(products, installationCategories);

  const groups = products
    .map((product) => product.group)
    .filter((group, index, all) => all.indexOf(group) === index);

  // The price list has the same sums as the positions; a single group is the total alone
  const summaryRows =
    (workTypeSummary || priceList) && products.length > 0
      ? [
          ...(priceList && groups.length < 2 ? [] : groups).map((group) =>
            summaryRow(
              'group',
              `summary-${group}`,
              t(`${translations.products}:groups:${group}`),
              getWorkTypeFigures(
                products.filter((product) => product.group === group),
                installationCategories
              )
            )
          ),
          summaryRow(
            'total',
            'summary-total',
            priceList
              ? t(`${translations.offers}:priceList.Total`)
              : t(`${translations.offers}:Whole offer total`),
            totalFigures
          ),
        ]
      : [];

  const rows: any[] = [...displayedProducts, ...summaryRows];

  // The key figures are highlighted as in the calculation window
  const keyFigure = 'theme-text-color key-figure';

  // Numbers are aligned by their last character so the columns are easy to read
  const numeric = {
    align: 'right',
    headerAlign: 'right',
    style: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  };
  const keyNumeric = {
    ...numeric,
    classes: keyFigure,
    headerClasses: keyFigure,
  };

  const orderColumn = {
    dataField: 'groupIndex',
    formatter: (
      groupIndex: number,
      row: { group: string; groupCount: number; summary?: string }
    ) =>
      row.summary !== undefined ? null : (
        <RowOrder
          count={row.groupCount}
          index={groupIndex}
          move={(fromIndex, toIndex) => move?.(row.group, fromIndex, toIndex)}
          reordering
        >
          {null}
        </RowOrder>
      ),
    headerStyle: () => ({ width: '4.5rem' }),
    text: '#',
  };

  const positionColumn = {
    dataField: 'position',
    // The code can be changed together with the order
    formatter: (cell: string, row: { id: number; summary?: string }) =>
      reordering && rename !== undefined && row.summary === undefined ? (
        <PositionCodeInput
          code={cell}
          save={(position) => rename(row.id, position)}
        />
      ) : (
        cell
      ),
    formatExtraData: reordering,
    text: t(`${translations.products}:Position`),
    style: { whiteSpace: 'nowrap' },
  };

  const printedPriceColumn = {
    ...keyNumeric,
    dataField: 'printedPrice',
    // The sums have no printed price
    formatter: (cell: string, row: any) =>
      cell === '' && row.summary === undefined ? (
        <span className="price-list-note">
          {t(`${translations.offers}:priceList.Note`)}
        </span>
      ) : (
        cell
      ),
    text: t(`${translations.offers}:priceList.Printed price`),
  };

  const columns = [
    ...(reordering ? [orderColumn] : []),
    ...(priceList ? [] : [positionColumn]),
    {
      classes: 'product-name',
      dataField: 'name',
      text: t(`${translations.products}:Product`),
    },
    ...(priceList ? [printedPriceColumn] : []),
    {
      ...numeric,
      dataField: 'quantity',
      text: t(`${translations.common}:Quantity`),
    },
    {
      ...keyNumeric,
      dataField: 'pricePerUnit',
      text: t(`${translations.offers}:Selling price per unit`),
    },
    {
      ...numeric,
      dataField: 'expensePerUnit',
      text: t(`${translations.offers}:Expense per unit`),
    },
    {
      ...keyNumeric,
      dataField: 'contributionMarginPerUnit',
      // The sign of the money shown in place of the €
      text: t(`${translations.offers}:Contribution margin per unit €`).replace(
        '€',
        symbol
      ),
    },
    {
      ...numeric,
      dataField: 'priceTotal',
      text: t(`${translations.offers}:Selling price`),
    },
    {
      ...numeric,
      dataField: 'expenseTotal',
      text: t(`${translations.offers}:Expenses total`),
    },
    {
      ...numeric,
      dataField: 'contributionMargin',
      text: `${t(`${translations.offers}:Contribution margin`)} ${symbol}`,
    },
    {
      ...keyNumeric,
      dataField: 'contributionMarginRatio',
      formatter: (cell: string, row: { marginClass: string }) => (
        <span className={`margin-ratio ${row.marginClass}`}>{cell}</span>
      ),
      text: `${t(`${translations.offers}:Contribution margin`)} %`,
    },
    {
      ...numeric,
      dataField: 'hours',
      text: t(`${translations.offers}:Hours`),
      // Installation work without average hourly earnings has no hours
      formatter: (
        cell: string,
        row: { averageHourlyEarningsMissing: boolean }
      ) =>
        row.averageHourlyEarningsMissing ? (
          <span
            className="missing-average-hourly-earnings"
            title={t(
              `${translations.offers}:Average hourly earnings missing info`
            )}
          >
            {t(`${translations.offers}:Average hourly earnings missing`)}
          </span>
        ) : (
          cell
        ),
    },
    {
      ...keyNumeric,
      dataField: 'contributionMarginPerHour',
      text: t(`${translations.offers}:Contribution margin per hour`),
    },
    // A row removed with one click, as the rows of the calculation window
    ...(remove === undefined || reordering
      ? []
      : [
          {
            dataField: 'remove',
            isDummyField: true,
            formatter: (cell: unknown, row: { id: number; summary?: string }) =>
              row.summary === undefined ? (
                <button
                  aria-label={removeLabel}
                  className="offer-products-remove"
                  onClick={(event) => {
                    // The click does not open the row
                    event.stopPropagation();
                    remove(row.id);
                  }}
                  title={removeLabel}
                  type="button"
                >
                  <img alt="" className="trash-icon" src={deleteIcon} />
                </button>
              ) : null,
            headerStyle: () => ({ width: '2.5rem' }),
            text: '',
          },
        ]),
  ];

  // A line before the first position of each work type; the sums by work type are
  // highlighted as an area of their own
  const getRowClasses = (
    row: { group: string; summary?: string },
    rowIndex: number
  ) => {
    if (row.summary !== undefined) {
      return [
        'work-type-summary',
        rows[rowIndex - 1]?.summary === undefined
          ? 'work-type-summary-start'
          : '',
        row.summary === 'total' ? 'work-type-summary-total' : '',
      ]
        .join(' ')
        .trim();
    }

    return rowIndex > 0 && rows[rowIndex - 1].group !== row.group
      ? 'work-type-start'
      : '';
  };

  const setSelectedRow = (index: number) => {
    // The sums are not a position
    if (rows[index].summary === undefined) {
      history.push(`/offers/products/${rows[index].id}/${type}`);
    }
  };

  return (
    <div className="offer-products-table">
      <BasicTableNoPagination
        columns={columns}
        data={rows}
        getRowClasses={getRowClasses}
        keyField="id"
        // While the order is changed a click does not open the position
        setSelectedRow={reordering ? undefined : setSelectedRow}
      />
      {workTypeSummary && totalFigures.missingAverageHourlyEarnings > 0 ? (
        <span className="missing-average-hourly-earnings">
          {t(
            `${translations.offers}:Positions missing average hourly earnings`,
            { count: totalFigures.missingAverageHourlyEarnings }
          )}
        </span>
      ) : null}
    </div>
  );
};

export default OfferProductsTable;
